import { Badge } from "@coval/ui/badge"
import { Skeleton } from "@coval/ui/skeleton"
import { AlertTriangle, CheckCircle2 } from "lucide-react"

interface UploadProgressViewProps {
  isUploading: boolean
}

/**
 * Skeleton/progress view shown while the conversation is uploading or
 * processing. `isUploading` true renders the uploading copy, false the
 * processing copy (callers only mount this in those two statuses).
 */
export function UploadProgressView({ isUploading }: UploadProgressViewProps) {
  return (
    <div className="p-8">
      <div className="text-center space-y-4">
        <div className="space-y-2">
          <Skeleton className="h-8 w-8 rounded-full mx-auto" />
          <Skeleton className="h-5 w-32 rounded mx-auto" />
          <Skeleton className="h-4 w-48 rounded mx-auto" />
        </div>
        <div className="space-y-1">
          <p className="text-sm font-medium text-coval-border-active">
            {isUploading ? "Uploading" : "Processing"}
          </p>
          <p className="text-sm text-coval-text-muted">
            {isUploading
              ? "Uploading to storage..."
              : "Processing with evaluation API..."}
          </p>
        </div>
        <Skeleton className="h-1 w-full max-w-xs mx-auto rounded-full" />
      </div>
    </div>
  )
}

interface UploadCompleteViewProps {
  conversationId: string
  tracesWarning: string
}

export function UploadCompleteView({
  conversationId,
  tracesWarning,
}: UploadCompleteViewProps) {
  return (
    <div className="p-8">
      <div className="flex flex-col items-center text-center space-y-4">
        <CheckCircle2 className="w-12 h-12 text-coval-success-text" />
        <div>
          <div className="text-lg font-semibold text-coval-success-text mb-2">
            Upload Successful
          </div>
          <Badge variant="success">Conversation ID: {conversationId}</Badge>
        </div>
        <div className="text-sm text-coval-text-muted">
          Conversation processed successfully
        </div>
        {tracesWarning && (
          <div className="flex items-start gap-2 text-sm text-coval-warning-on-surface bg-coval-warning-surface p-3 rounded-lg max-w-sm">
            <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />
            <span>{tracesWarning}</span>
          </div>
        )}
      </div>
    </div>
  )
}
